// "Are you sure?" for actions that need more than a click to undo.
//
// One dialog, four call sites (admin Team, Developers, Project Managers, PM
// Team) — they were drifting: three had a confirm modal with slightly different
// wording and the admin Team page had NO confirmation at all, deleting straight
// off the trash icon that sits two pixels from Edit.
//
// The password is the ACTOR's own, checked server-side. It isn't theatre: the
// button revokes someone's access and kills their tokens, and re-authenticating
// is what stops a mis-click or a borrowed laptop from doing it.

import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, Lock } from "lucide-react";
import Modal from "@/components/dashboard/Modal";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";

/**
 * @param {boolean}  open
 * @param {string}   title
 * @param {node}     children     what's about to happen, in plain words
 * @param {string}   confirmLabel
 * @param {string}   error        server-side failure (a wrong password lands here)
 * @param {boolean}  busy
 * @param {function} onConfirm    (password) => void
 * @param {function} onClose
 */
export default function ConfirmWithPassword({
  open,
  title = "Are you sure?",
  children,
  confirmLabel = "Confirm",
  error = "",
  busy = false,
  onConfirm,
  onClose,
}) {
  const [password, setPassword] = useState("");
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);

  // Never leave a typed password sitting in state behind a closed dialog.
  useEffect(() => {
    if (!open) {
      setPassword("");
      setTouched(false);
      return undefined;
    }
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [open]);

  function submit() {
    setTouched(true);
    if (!password.trim() || busy) return;
    onConfirm?.(password);
  }

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onClose}
      title={title}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={submit}
            disabled={busy || !password.trim()}
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50/70 p-4 text-sm text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>{children}</div>
        </div>

        <div>
          <Input
            ref={inputRef}
            label="Confirm with your password"
            icon={Lock}
            type="password"
            // NOT "current-password": that marks this a sign-in, and the
            // browser then fills the saved username into the nearest text
            // input on the page — the table's search box, behind this dialog.
            autoComplete="new-password"
            placeholder="Your account password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            disabled={busy}
          />
          <p className="mt-1.5 text-xs text-slate-400">
            Your own password — not theirs.
          </p>
        </div>

        {(error || (touched && !password.trim())) && (
          <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
            {error || "Enter your password to continue."}
          </p>
        )}
      </div>
    </Modal>
  );
}
